import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import styled, {css} from "styled-components";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import ReactCodeInput from "react-verification-code-input";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";

// Utils
import {AuthContext} from "../contexts/auth.js";
import useMountedState from "../hooks/useMountedState.js";
import useApi from "../hooks/useApi.js";

// Components
import {InputPassword, InputCheck, InputError} from "../components/form/FormInputs.js";
import Footer from "./general/Footer.js";

// Style
import {border, heroTheme, pad, radius} from "../style/components/variables.js";
import {flex, z} from "../style/components/mixins.js";
import {
  Button,
  Error,
  Form,
  FormField,
  FormGroup,
  Heading,
  Inline,
  Input,
  Small,
  Text
} from "../style/components/general.js";
import voice from "../style/components/typography.js";

const setPasswordSchema = yup.object().shape({
  password: yup
    .string()
    .required("Please provide password")
    .min(6, "Password must be at least 6 characters"),
  confirmed: yup
    .string()
    .required("Please provide password")
    .min(6, "Password must be at least 6 characters")
    .test({
      message: "Passwords must match",
      test: (password, ctx) => password === ctx.parent.password
    }),
  certification: yup.bool().not([false], "Please certify your submission.")
});

const emailSchema = yup.object().shape({
  email: yup.string().email("Invalid email address").required("Please provide email")
});

const FirstLogin = () => {
  const navigate = useNavigate();

  const isMounted = useMountedState();
  const [searchParams] = useSearchParams();

  const [code, setCode] = useState();
  const [inviteVersion, setInviteVersion] = useState();
  const [authValueError, setAuthValueError] = useState();

  const {pathname} = useLocation();

  const {currentUser, resetPassword, tempUser, setTempUser} = useContext(AuthContext);

  const {api: apiValidate} = useApi("validate-token");
  const {api: apiInviteVersion} = useApi("invite-version");

  const setPasswordForm = useForm({
    resolver: yupResolver(setPasswordSchema)
  });

  const emailForm = useForm({
    resolver: yupResolver(emailSchema)
  });

  const {handleSubmit: handleSubmitPassword} = setPasswordForm;
  const {
    handleSubmit: handleSubmitEmail,
    register,
    formState: {errors},
    watch
  } = emailForm;

  const watchEmail = watch("email");

  const handleReset = ({password}) => resetPassword(password, true);

  useEffect(() => () => setTempUser(undefined), [setTempUser]);

  const tryValidate = useCallback(
    ({email, token, authValue}) =>
      apiValidate.callGet(null, {email, token, code: authValue}).then(({status, data}) => {
        if (status === 200 && data?.currentUser) {
          setTempUser(data.currentUser);
          setCode(undefined);
        }
      }),
    [apiValidate, setTempUser]
  );

  const submitEmail = ({email, authValue}) => {
    if (!authValue) {
      if (inviteVersion) setAuthValueError("Please provide temporary password.");
      else
        apiInviteVersion.callGet(null, {email}).then(({status, data}) => {
          if (status === 200 && data?.inviteVersion) setInviteVersion(data.inviteVersion);
        });
      return;
    }
    tryValidate({email, authValue});
  };

  const token = useMemo(() => searchParams.get("token"), [searchParams]);

  // redirect to Facilities Dashboard on Signin
  useEffect(() => {
    if (isMounted() && currentUser && currentUser.isAccepted) navigate("/facilities");
  }, [isMounted, currentUser, pathname, navigate]);

  useEffect(() => {
    if (!tempUser && code && code?.length === 5 && (token || watchEmail))
      tryValidate({email: watchEmail, token, authValue: code});
  }, [navigate, apiValidate, tempUser, setTempUser, code, token, watchEmail, tryValidate]);

  return (
    <ResetPasswordPage clipped>
      <Header>
        <Logo src={`${process.env.PUBLIC_URL}/hero-services.png`} alt="Hero Services Logo" />
      </Header>
      <ScrollContainer>
        <FormWrapper>
          <FormInnerWrapper>
            <PageHeading>Welcome to Hero Builder™!</PageHeading>
            {!tempUser ? (
              <Inline column nogap>
                {token || inviteVersion === 2 ? (
                  <>
                    <Text center>
                      {inviteVersion === 2 ? "Next" : "To get started"}, please provide the
                      confirmation code included in your invitation email.
                    </Text>
                    <br />
                    <br />
                    <ReactCodeInput fields={5} type="number" onChange={value => setCode(value)} />
                  </>
                ) : (
                  <Inline column nogap>
                    <Text center>To get started, please enter your email below.</Text>
                    <br />
                    <br />
                    <FormProvider {...emailForm}>
                      <Form onSubmit={handleSubmitEmail(submitEmail)} noValidate>
                        <FormField>
                          <Input
                            type="email"
                            placeholder="Email..."
                            autoComplete="username"
                            {...register("email", {required: true})}
                          />
                          <InputError name="email" errors={errors} />
                        </FormField>
                        {inviteVersion === 1 && (
                          <FormField>
                            <InputPassword name="authValue" placeholder="Temporary Password..." />
                            {authValueError && <Error>{authValueError}</Error>}
                          </FormField>
                        )}
                        <Reset type="submit">Continue</Reset>
                      </Form>
                    </FormProvider>
                  </Inline>
                )}
              </Inline>
            ) : (
              <>
                <Text center>
                  Please agree to Terms & Conditions and create a new password to get started.
                </Text>
                <FormProvider {...setPasswordForm}>
                  <Form onSubmit={handleSubmitPassword(handleReset)} noValidate>
                    <FormGroup>
                      <FormField>
                        <InputPassword
                          name="password"
                          label="Password"
                          placeholder="New password"
                          required
                        />
                      </FormField>
                      <FormField>
                        <InputPassword name="confirmed" placeholder="Confirm password" required />
                      </FormField>
                      <FormField>
                        <InputCheck name="certification" label="Certification" required>
                          The following is equivalent to your digital signature.
                          <br />
                          <Small>
                            I, {tempUser?.firstName} {tempUser?.lastName}, agree and understand that
                            by checking this box that all electronic signatures are the legal
                            equivalent of my manual/handwritten signature and I consent to be
                            legally bound to this agreement.
                          </Small>
                        </InputCheck>
                      </FormField>
                      <Reset type="submit">Confirm New Password</Reset>
                    </FormGroup>
                  </Form>
                </FormProvider>
              </>
            )}

            <br />
            <StyledButton onClick={() => navigate("/signin")} plain>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Sign In
            </StyledButton>
            {tempUser && <BottomSpacer />}
          </FormInnerWrapper>
        </FormWrapper>
        <ScrollGradient />
      </ScrollContainer>
      <Footer relative />
    </ResetPasswordPage>
  );
};

// Style Overrides
const PageHeading = styled(Heading)`
  color: ${heroTheme.secondary};
  margin-bottom: ${pad}px;
`;

const ResetPasswordPage = styled.section`
  ${flex("column", "nowrap", "space-between", "center")};
  height: 100vh;
`;

const ScrollContainer = styled.div`
  position: relative;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const FormWrapper = styled.section`
  ${flex("column", "nowrap", "center", "center")}
  overflow-y: auto;
  width: 100%;
  height: 100%;
`;

const ScrollGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60px;
  pointer-events: none;
  z-index: ${z("above")};
  background: linear-gradient(to bottom, transparent, ${({theme}) => theme.bg || "#fff"} 100%);
`;

const FormInnerWrapper = styled.div`
  ${flex("column", "nowrap", "flex-start", "center")}
  max-width: 550px;
  padding: ${pad * 2}px;

  width: 100%;
  height: 100%;
  max-height: max-content;
`;

const BottomSpacer = styled.div`
  height: 30px;
  flex-shrink: 0;
  width: 100%;
`;

const Reset = styled.button`
  border: 0;
  margin-top: ${pad * 2}px;
  align-self: center;
  padding: ${pad}px ${pad * 4}px;
  background-color: ${heroTheme.secondary};
  color: ${heroTheme.tertiary};
  border-radius: ${radius};
  width: max-content;
`;

const Header = styled.div`
  width: 100%;
  align-self: start;
  display: flex;
  background: ${heroTheme.secondary};
  z-index: ${z("top")};
  flex-shrink: 0;

  ${props =>
    props.theme.name === "dark" &&
    css`
      border-bottom: ${border} solid ${heroTheme.tertiary};
    `}
`;

const Logo = styled.img`
  height: 75px;
  margin: ${pad}px auto;
`;

const StyledButton = styled(Button)`
  ${voice.small};
  margin-bottom: 40px;

  svg {
    fill: ${({theme}) => theme.secondary};
  }
`;

export default FirstLogin;
